import dayjs, { type Dayjs } from "dayjs";

import { cn } from "@fitness-app/utils/src/styles/cn";

import { CalendarDayCell } from "./CalendarDayCell";
import { getVisibleDays, toDayKey, WEEKDAY_LABELS, type CalendarEventsProps } from "./calendarModel";

// 4 wiersze po 24 px + nagłówek dnia: każda komórka ma tę samą wysokość niezależnie od
// liczby wydarzeń, więc siatka nie „skacze" między tygodniami.
const MAX_EVENTS_PER_DAY = 4;

interface MonthViewProps<T> extends CalendarEventsProps<T> {
  date: Dayjs;
}

export const MonthView = <T,>({
  date,
  eventsByDay,
  isDayDimmed,
  dayMenu,
  onDayClick,
  ...cellProps
}: MonthViewProps<T>) => {
  const days = getVisibleDays("month", date);
  const today = dayjs();
  const lastRowStart = days.length - 7;

  return (
    <div className="overflow-hidden rounded-xl border border-solid border-gray-200 bg-white">
      <div className="grid grid-cols-7 border-0 border-b border-solid border-gray-200 bg-gray-50/80">
        {WEEKDAY_LABELS.map((label) => (
          <div key={label} className="px-3 py-2 text-xs font-medium uppercase tracking-wide text-gray-500">
            {label}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {days.map((day, index) => {
          const inMonth = day.isSame(date, "month");
          const isToday = day.isSame(today, "day");

          return (
            <CalendarDayCell
              key={toDayKey(day)}
              {...cellProps}
              day={day}
              events={inMonth ? (eventsByDay.get(toDayKey(day)) ?? []) : []}
              maxEvents={MAX_EVENTS_PER_DAY}
              dayMenu={inMonth ? dayMenu : undefined}
              onDayClick={inMonth ? onDayClick : undefined}
              className={cn(
                "min-h-[136px] border-0 border-solid border-gray-200",
                index % 7 !== 6 && "border-r",
                index < lastRowStart && "border-b",
                (!inMonth || isDayDimmed?.(day)) && "bg-gray-50/60",
              )}
              header={
                <span
                  className={cn(
                    "flex size-6 items-center justify-center rounded-full text-xs font-semibold tabular-nums",
                    isToday ? "bg-blue-600 text-white" : inMonth ? "text-gray-800" : "text-gray-300",
                  )}
                >
                  {day.date()}
                </span>
              }
            />
          );
        })}
      </div>
    </div>
  );
};
